import type {
  Lead,
  LeadResponse,
  LeadsDueResponse,
  ReportLeadErrorDto,
} from "@/types/campaign";
import { ThirdPartyImportResult } from "@/types/integrations";
import { LassoErrorCode, lassoExceptionSchema } from "@/types/lasso-exception";
import {
  api,
  baseUrl,
  buildQueryString,
  isLassoException,
  LassoException,
  type PagedResult,
} from "@/utils/api";
import {
  infiniteQueryOptions,
  keepPreviousData,
  queryOptions,
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import { notFound } from "@tanstack/react-router";
import { addHours } from "date-fns";
import { HTTPError } from "ky";
import { z } from "zod";

export const listLeadsParamsSchema = z
  .object({
    page: z.number(),
    pageSize: z.number(),
    createdBefore: z.date(),
    name: z.string(),
    lassoId: z.string(),
    responsibleId: z.array(z.string()),
    campaignId: z.array(z.number()),
    status: z.array(z.string()),
    configurationId: z.array(z.number()),
    configurationName: z.array(z.string()),
    orderBy: z.string(),
    desc: z.boolean(),
  })
  .partial();

export type ListLeadsParams = z.infer<typeof listLeadsParamsSchema>;

const listLeadsErrorTypes = {
  error: () => "Noget gik galt",
  nameTooShort: () => "Søgeordet skal være mindst 3 tegn langt",
  nameTooBroad: (name: string) =>
    `For mange resultater for "${name}", prøv at præcisere søgningen`,
} as const;

type ListLeadsErrorTypes = typeof listLeadsErrorTypes;
type ListLeadsErrorType = keyof ListLeadsErrorTypes;

export type LeadNotes = {
  id: string;
  content: string;
  timestamp: string;
  name: string;
  userId: string;
  shared: boolean;
  orgId: string;
};

export class ListLeadsError<
  T extends ListLeadsErrorType = ListLeadsErrorType,
> extends Error {
  public name: ListLeadsErrorType;

  constructor(name: T, ...args: Parameters<ListLeadsErrorTypes[T]>) {
    const createMessage = listLeadsErrorTypes[name] as (
      ...args: Parameters<ListLeadsErrorTypes[T]>
    ) => string;
    super(createMessage(...args));
    this.name = name;
  }
}

export const leadsApi = {
  list: async (params: ListLeadsParams) => {
    if (params.name && params.name.length < 3)
      throw new ListLeadsError("nameTooShort");
    const response = await api.get<PagedResult<Lead>>(
      `apps/sales/leads${buildQueryString({
        ...params,
        createdBefore: params.createdBefore?.toISOString(),
      })}`,
      { throwHttpErrors: false },
    );
    const json = await response.json();
    if (!response.ok) {
      const { data: lassoException } = lassoExceptionSchema.safeParse(json);
      if (!lassoException) throw new ListLeadsError("error");
      switch (lassoException.errorCode) {
        case LassoErrorCode.TooMuchDataException:
          if (params.name)
            throw new ListLeadsError("nameTooBroad", params.name);
          break;

        default:
          throw new Error(
            `Unhandled error code: ${lassoException.errorCode}, HTTP status: ${response.status}, message: ${lassoException.errorMessage}`,
          );
      }
    }
    return json;
  },
  put: async (id: number, data: Partial<Lead>) => {
    return await api
      .put<LeadResponse>(`apps/sales/leads/${id}`, {
        json: data,
      })
      .json();
  },
  get: async (id: number) => {
    return await api
      .get<LeadResponse>(`apps/sales/leads/${id}`, {
        hooks: {
          beforeRetry: [
            async ({ request, error }) => {
              if (error instanceof HTTPError) {
                if (error.response.status === 404) {
                  // Throwing error within hook to skip retries (https://github.com/sindresorhus/ky#:~:text=You%20can%20prevent,docs%20for%20details).)
                  throw notFound({ data: { resource: "leadet" } });
                }
                if (error.response.status === 401) {
                  // Modify request to skip third party link and retry
                  const body = await error.response.json<LassoException>();
                  if (body.errorMessage?.includes("connection info")) {
                    const url = new URL(request.url);
                    if (url.searchParams.get("skipThirdPartyLink") === "true") {
                      // Already retried with skipThirdPartyLink, do not retry again
                      throw error;
                    }
                    url.searchParams.set("skipThirdPartyLink", "true");
                    return new Request(url, request);
                  }
                }
              }
            },
          ],
        },
      })
      .json();
  },
  export: async (
    leadId: number,
    provider: string,
    exportInput: Record<string, string>,
  ) => {
    return await api
      .post<ThirdPartyImportResult>(
        `apps/sales/leads/${leadId}/export/${provider}`,
        {
          json: { exportInput },
        },
      )
      .json();
  },
  report: async (leadId: number, payload: ReportLeadErrorDto) => {
    return await api
      .post<void>(`apps/sales/leads/${leadId}/report`, { json: payload })
      .json();
  },
  download: async (params: ListLeadsParams) => {
    const response = await api.get(
      `apps/sales/leads/download${buildQueryString({
        ...params,
        createdBefore: params.createdBefore?.toISOString(),
      })}`,
    );
    const blob = await response.blob();
    return { blob };
  },
  generateDownloadToken: async (params: ListLeadsParams) => {
    return await api
      .get<{ token: string; expiration: string }>(
        `apps/sales/leads/download/token/generate${buildQueryString({
          ...params,
          createdBefore: params.createdBefore?.toISOString(),
        })}`,
      )
      .json();
  },
  retryExport: async (leadId: number) => {
    return await api
      .post<ThirdPartyImportResult>(`apps/sales/leads/${leadId}/export/retry`)
      .json();
  },
  getDue: async (params?: {
    before?: string;
    after?: string;
    userIds?: string[];
    campaignIds?: number[];
    lassoId?: string;
    name?: string;
    skipNameRetrieval?: boolean;
    pageSize?: number;
  }) => {
    return await api
      .get<LeadsDueResponse>(
        `apps/sales/leads/due${buildQueryString(params ?? {})}`,
      )
      .json();
  },
};

export type LeadNotesError = {
  errorCode: number;
  message?: string;
};

export type LeadNotesResponse = {
  notes: LeadNotes[];
  error: LeadNotesError | null;
};

export const leadsNotesApi = {
  getNotes: async (leadId: number): Promise<LeadNotesResponse> => {
    const response = await api.get<LeadNotes[]>(
      `modules/storage/document/LassoSalesLead/${leadId}`,
      {
        retry: {
          shouldRetry: async ({ error }) => {
            if (error instanceof HTTPError && error.response.status === 400) {
              const resp = await error.response.json();
              // No storage provider configured, skip retry
              if (isLassoException(resp) && resp.errorCode === 6) return false;
            }
            return true;
          },
        },
      },
    );
    const json = await response.json();
    if (!response.ok) {
      return {
        error: { errorCode: response.status, message: response.statusText },
        notes: [],
      };
    }
    return { notes: json as LeadNotes[], error: null };
  },
  addNote: async (
    leadId: number,
    vars: { content: string; userName: string },
  ) => {
    return await api.post<LeadNotes>(
      `modules/storage/document/LassoSalesLead/${leadId}`,
      {
        json: {
          content: vars.content,
          name: vars.userName,
          shared: true,
        },
      },
    );
  },
  editNote: async (leadId: number, vars: LeadNotes) => {
    return await api.put<LeadNotes>(
      `modules/storage/document/LassoSalesLead/${leadId}/${vars.id}`,
      {
        json: vars,
      },
    );
  },
  deleteNote: async (leadId: number, documentId: string) => {
    return await api.delete(
      `modules/storage/document/LassoSalesLead/${leadId}/${documentId}`,
    );
  },
};

export const useLeadsNotesQuery = (leadId: number) => {
  return useQuery({
    queryKey: ["leadNotes", leadId],
    queryFn: () => leadsNotesApi.getNotes(leadId),
    enabled: !!leadId,
    retry: false, // retries are handled by Ky HTTP wrapper
  });
};

export const useAddLeadNoteMutation = (leadId: number) => {
  const qc = useQueryClient();

  return useMutation({
    mutationKey: ["addLeadNote", leadId],
    mutationFn: (vars: { content: string; userName: string }) => {
      return leadsNotesApi.addNote(leadId, vars);
    },
    onMutate: async (vars) => {
      await qc.cancelQueries({ queryKey: ["leadNotes", leadId] });

      const previousNotes = qc.getQueryData<{
        notes: LeadNotes[];
        error: unknown;
      }>(["leadNotes", leadId]);

      const optimisticNote: LeadNotes = {
        id: `temp-${Date.now()}`,
        content: vars.content,
        timestamp: new Date().toISOString(),
        name: vars.userName ?? "",
        userId: "0",
        shared: true,
        orgId: "",
      };

      qc.setQueryData<{ notes: LeadNotes[]; error: unknown }>(
        ["leadNotes", leadId],
        (old) => ({
          notes: [...(old?.notes ?? []), optimisticNote],
          error: null,
        }),
      );

      return { previousNotes };
    },
    onError: (_err, _, context) => {
      if (context?.previousNotes) {
        qc.setQueryData(["leadNotes", leadId], context.previousNotes);
      }
    },
    onSettled: () => {
      qc.invalidateQueries({ queryKey: ["leadNotes", leadId] });
    },
  });
};

export const useEditLeadNoteMutation = (leadId: number) => {
  const qc = useQueryClient();

  return useMutation({
    mutationKey: ["editLeadNote", leadId],
    mutationFn: (vars: LeadNotes) => leadsNotesApi.editNote(leadId, vars),
    onMutate: async (vars) => {
      await qc.cancelQueries({ queryKey: ["leadNotes", leadId] });

      const previousNotes = qc.getQueryData<{
        notes: LeadNotes[];
        error: unknown;
      }>(["leadNotes", leadId]);

      qc.setQueryData<{ notes: LeadNotes[]; error: unknown }>(
        ["leadNotes", leadId],
        (old) => {
          if (!old) return { notes: [vars], error: null };

          const noteIndex = old.notes.findIndex((note) => note.id === vars.id);
          if (noteIndex === -1) return old;

          const newNotes = old.notes.filter((note) => note.id !== vars.id);
          newNotes.push(vars);

          return { notes: newNotes, error: null };
        },
      );

      return { previousNotes };
    },
    onError: (_err, _, context) => {
      if (context?.previousNotes) {
        qc.setQueryData(["leadNotes", leadId], context.previousNotes);
      }
    },
  });
};

export const useDeleteLeadNoteMutation = (leadId: number) => {
  const qc = useQueryClient();

  return useMutation({
    mutationKey: ["deleteLeadNote", leadId],
    mutationFn: (documentId: string) =>
      leadsNotesApi.deleteNote(leadId, documentId),
    onMutate: async (documentId) => {
      const previousNotes = qc.getQueryData<{
        notes: LeadNotes[];
        error: unknown;
      }>(["leadNotes", leadId]);

      qc.setQueryData<{ notes: LeadNotes[]; error: unknown }>(
        ["leadNotes", leadId],
        (old) => ({
          notes: old?.notes.filter((note) => note.id !== documentId) ?? [],
          error: null,
        }),
      );

      return { previousNotes };
    },
    onError: (_err, _, context) => {
      if (context?.previousNotes) {
        qc.setQueryData(["leadNotes", leadId], context.previousNotes);
      }
    },
  });
};
export const createLeadsQueryOptions = (
  params: Omit<ListLeadsParams, "page">,
) => {
  params = listLeadsParamsSchema.omit({ page: true }).strip().parse(params);
  return infiniteQueryOptions({
    queryKey: ["leads", params] as const,
    queryFn: async (
      opts,
    ): Promise<PagedResult<Lead> & { error?: ListLeadsError }> => {
      try {
        return await leadsApi.list({
          ...params,
          page: opts.pageParam,
        });
      } catch (error) {
        // Certain errors are expected and should be returned instead of thrown.
        if (error instanceof ListLeadsError) {
          switch (error.name) {
            case "nameTooShort":
            case "nameTooBroad": {
              return {
                page: opts.pageParam,
                pageSize: params.pageSize ?? 0,
                results: [],
                resultsFound: 0,
                resultsReturned: 0,
                totalPages: 1,
                error,
              };
            }
          }
        }
        throw error;
      }
    },
    initialPageParam: 1,
    throwOnError: true,
    placeholderData: keepPreviousData,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    getNextPageParam: (lastPage) =>
      lastPage.page < lastPage.totalPages ? lastPage.page + 1 : undefined,
  });
};

export const useLeadDownLoad = (params: ListLeadsParams) => {
  return useQuery({
    queryKey: ["leadDownload", params],
    queryFn: () => leadsApi.download(params),
    enabled: false,
  });
};

export const useLeadTokenDownload = (params: ListLeadsParams) => {
  const { refetch, isFetching } = useQuery({
    queryKey: ["leadDownloadToken", params],
    queryFn: async () => {
      const { token } = await leadsApi.generateDownloadToken(params);
      window.open(
        `${baseUrl}/apps/sales/leads/download/token?t=${token}`,
        "_blank",
      );
      return null;
    },
    enabled: false,
  });
  return { download: refetch, isDownloading: isFetching };
};

export const useLeadsQuery = (params: Omit<ListLeadsParams, "page">) =>
  useInfiniteQuery(createLeadsQueryOptions(params));

export const createLeadQueryOptions = (id: number) =>
  queryOptions({
    queryKey: ["lead", id],
    queryFn: () => leadsApi.get(id),
    throwOnError: true,
    staleTime: 5 * 60 * 1000, // Consider data fresh for 5 minutes
    gcTime: 30 * 60 * 1000, // Keep unused data in cache for 30 minutes
    placeholderData: keepPreviousData,
  });

export const useLeadQuery = (id: number) =>
  useQuery(createLeadQueryOptions(id));

export const useReportLeadErrorMutation = () => {
  const qc = useQueryClient();

  return useMutation({
    mutationKey: ["leadReport"],
    mutationFn: (vars: { leadId: number; data: ReportLeadErrorDto }) =>
      leadsApi.report(vars.leadId, vars.data),

    onSuccess: (_data, { leadId }) => {
      qc.invalidateQueries({ queryKey: ["lead", leadId] });
      qc.invalidateQueries({ queryKey: ["leads"] });
    },
  });
};

export const createDueLeadsQueryOptions = (params: {
  userIds?: string[];
  pageSize?: number;
  campaignIds?: number[];
  lassoId?: string;
  name?: string;
  skipNameRetrieval?: boolean;
}) => {
  return queryOptions({
    queryKey: ["leads", "due", params] as const,
    queryFn: () => {
      const now = new Date();
      const hourLater = addHours(now, 2);
      return leadsApi.getDue({
        after: now.toISOString(),
        before: hourLater.toISOString(),
        ...params,
      });
    },
    refetchInterval: import.meta.env.PROD ? 60000 : false, // Refetch every minute in production, disable refetching in development
    refetchOnMount: true,
    refetchOnWindowFocus: true,
  });
};

export const useDueLeadsQuery = (params?: {
  userIds?: string[];
  campaignIds?: number[];
  lassoId?: string;
  name?: string;
  skipNameRetrieval?: boolean;
  pageSize?: number;
}) => useQuery(createDueLeadsQueryOptions(params ?? {}));
